import { Box, Icon, SxProps, Theme, visuallyHidden } from "@outschool/backpack";
import { faBars, faSearch } from "@outschool/icons";
import { TFunction, useTranslation } from "@outschool/localization";
import {
  addParamsToUrl,
  forSellerOrgPath,
  leadActivitiesPath,
  pathFromLocation,
  searchPath,
  sellerOrgPath,
  teachPath,
  teachTipsUrl
} from "@outschool/routes";
import { AuthTrigger, useSession } from "@outschool/ui-auth";
import { ExternalLink, TrackedButton } from "@outschool/ui-components-shared";
import {
  CategoryNavbar,
  NavHelpLink,
  useMobileSearchBar
} from "@outschool/ui-components-website";
import {
  ConditionalTrackingPageSection,
  Container
} from "@outschool/ui-legacy-component-library";
import { Screen, useOnScreen } from "@outschool/ui-utils";
import React from "react";
import { useLocation } from "react-router-dom";

import * as Routes from "../../../shared/Routes";
import { usePricingOfferAdmin } from "../../hooks/CurrentUser";
import { UserMode, useUserMode, useUserModeRootPath } from "../../lib/UserMode";
import Link from "../Link";
import { DesktopHeaderNavigation } from "./DesktopHeaderNav";
import { EducatorModeToolbar } from "./EducatorModeToolbar";
import GlobalSearchBar, { MobileSearchBar } from "./GlobalSearchBar";
import HeaderLogo from "./HeaderLogo";
import { HeaderMenu, HeaderMenuButtonProps } from "./HeaderMenu";
import HeaderNavItem from "./HeaderNavItem";

interface Props {
  trackingUniqueId?: string;
  isTransparent?: boolean;
  isWhiteText?: boolean;
  allowGlobalNavbar?: boolean;
}

export default function Header({
  trackingUniqueId,
  isTransparent,
  isWhiteText = true,
  allowGlobalNavbar = false
}: Props) {
  const userMode = useUserMode();
  const isEducatorMode = userMode === UserMode.Educator;
  const includeGlobalNavbar = !isEducatorMode && allowGlobalNavbar;
  const { t } = useTranslation("client\\components\\nav\\Header");

  return (
    <ConditionalTrackingPageSection
      name="header"
      uniqueId={trackingUniqueId ? trackingUniqueId : "header"}
    >
      {setImpressionNode => (
        <Box ref={setImpressionNode}>
          {isTransparent ? (
            <TransparentHeader
              isWhiteText={isWhiteText}
              includeGlobalNavbar={includeGlobalNavbar}
              t={t}
            />
          ) : (
            <StandardHeader
              hideFindClasses={isEducatorMode}
              includeGlobalNavbar={includeGlobalNavbar}
            />
          )}
        </Box>
      )}
    </ConditionalTrackingPageSection>
  );
}

type HeaderFlexContainer = React.PropsWithChildren<{
  sx?: SxProps;
  extended?: boolean;
}>;

function HeaderFlexContainer({
  sx = {},
  extended = false,
  ...props
}: HeaderFlexContainer) {
  return (
    <Container size={extended ? "large" : "medium"}>
      <Box
        flex
        sx={[
          {
            alignItems: "center",
            justifyContent: "space-between"
          },
          ...(Array.isArray(sx) ? sx : [sx])
        ]}
        {...props}
      />
    </Container>
  );
}

function StandardHeaderLeft() {
  const rootPath = useUserModeRootPath();
  return (
    <Link to={rootPath} trackingName="nav_logo" reloadDocument>
      <HeaderNavItem sx={{ paddingX: 0, display: "flex" }}>
        <HeaderLogo isWhiteText={false} />
      </HeaderNavItem>
    </Link>
  );
}

function ListingsPageLink({ t }: { t: TFunction }) {
  return (
    <Link to={searchPath()} trackingName="nav_find_classes">
      <HeaderNavItem>
        <Icon
          icon={faSearch}
          sx={{
            fontSize: "1.333em"
          }}
        />
      </HeaderNavItem>
      <span style={visuallyHidden}>{t`Search`}</span>
    </Link>
  );
}

function MobileHeaderMenuButton({ isWhiteText }: { isWhiteText?: boolean }) {
  const { t } = useTranslation("client\\components\\nav\\Header");
  const [numCloseCount, setNumCloseCount] = React.useState(0);

  return (
    <HeaderMenu
      onClose={() => {
        setNumCloseCount(prev => prev + 1);
      }}
      opensDrawer
    >
      {({ isOpen: _isOpen, buttonRef, ...props }: HeaderMenuButtonProps) => (
        <TrackedButton
          variant="link"
          trackingName="mobile_header_menu_open_button"
          trackingUniqueId={`mobile_header_menu_open_button-${numCloseCount}`}
          trackingSharedProperties={{
            numCloseCount
          }}
          aria-label={t("Menu")}
          ref={buttonRef}
          {...props}
        >
          <Icon
            icon={faBars}
            sx={{
              fontSize: "1.333em",
              color: isWhiteText ? "common.white" : "gray"
            }}
          />
        </TrackedButton>
      )}
    </HeaderMenu>
  );
}

function StandardHeaderRight({ hideFindClasses, t }) {
  const isMobile = Screen.useIsMobile();
  return (
    <Box data-test-id="header-nav">
      {isMobile ? (
        <Box
          flex
          sx={{
            gap: "1.5em",
            paddingY: "0.5em"
          }}
        >
          {!hideFindClasses && <ListingsPageLink t={t} />}
          <MobileHeaderMenuButton />
        </Box>
      ) : (
        <DesktopHeaderNavigation />
      )}
    </Box>
  );
}

const HeaderToolbar = () => {
  const isMobile = Screen.useIsMobile();
  const isEducatorMode = useUserMode() === UserMode.Educator;

  if (isMobile && !isEducatorMode) {
    return <MobileSearchBar />;
  }

  if (!isMobile) {
    if (isEducatorMode) {
      return <EducatorModeToolbar />;
    }
    return <GlobalSearchBar />;
  }

  return null;
};

interface StandardHeaderProps {
  sx?: any;
  hideFindClasses?: boolean;
  leftChildren?: any;
  rightChildren?: any;
  includeGlobalNavbar?: boolean;
}
export const StandardHeader = ({
  sx,
  hideFindClasses,
  leftChildren,
  rightChildren,
  includeGlobalNavbar
}: StandardHeaderProps) => {
  const ref = React.useRef<HTMLElement>(null);
  const { t } = useTranslation("client\\components\\nav\\Header"); //StickyMobileHeader imports this as well
  const { currentUser } = useSession();

  const { setIsHeaderOnScreen } = useMobileSearchBar();
  const isOnScreen = useOnScreen(ref.current);
  const isOrg = currentUser && currentUser.sellerOrg;
  const userMode = useUserMode();
  const isEducatorMode = userMode === UserMode.Educator;
  const isOfferAdmin = usePricingOfferAdmin();

  React.useEffect(() => {
    setIsHeaderOnScreen(isOnScreen);
  }, [setIsHeaderOnScreen, isOnScreen]);

  if (leftChildren === undefined) {
    // if null, omit left header
    leftChildren = <StandardHeaderLeft />;
  }
  if (rightChildren === undefined) {
    // if null, omit right header
    rightChildren = (
      <StandardHeaderRight hideFindClasses={hideFindClasses} t={t} />
    );
  }
  return (
    <Box
      ref={ref}
      sx={{
        zIndex: 201,
        position: "relative",
        backgroundColor: "common.white",
        boxShadow: "0px 1px 12px rgba(0, 0, 0, 0.08)",
        ...sx
      }}
    >
      <HeaderFlexContainer
        extended={Boolean(isOrg || (isOfferAdmin && isEducatorMode))}
        sx={{
          gap: "1.5em"
        }}
      >
        {leftChildren}
        <HeaderToolbar />
        {rightChildren}
      </HeaderFlexContainer>
      {includeGlobalNavbar && (
        <CategoryNavbar
          sx={(theme: Theme) => ({
            [theme.breakpoints.down("md")]: {
              borderTop: "1px solid"
            },
            borderColor: "#808080"
          })}
        />
      )}
    </Box>
  );
};

function TransparentHeader({ isWhiteText, includeGlobalNavbar, t }) {
  const isMobile = Screen.useIsMobile();
  const rootPath = useUserModeRootPath();
  return (
    <Box
      sx={{
        backgroundColor: "common.white"
      }}
    >
      <HeaderFlexContainer>
        <Box flex>
          <Link to={rootPath} trackingName="nav_logo" reloadDocument>
            <HeaderNavItem
              variant={isWhiteText ? "transparent" : "standard"}
              sx={
                !isWhiteText && {
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.5)"
                  }
                }
              }
            >
              <HeaderLogo isWhiteText={isWhiteText} />
            </HeaderNavItem>
          </Link>
        </Box>

        {isMobile ? (
          <MobileHeaderMenuButton isWhiteText={isWhiteText} />
        ) : (
          <DesktopTransparentHeaderNav isWhiteText={isWhiteText} t={t} />
        )}
      </HeaderFlexContainer>

      {includeGlobalNavbar && (
        <CategoryNavbar
          sx={(theme: Theme) => ({
            [theme.breakpoints.down("md")]: {
              borderTop: "1px solid"
            },
            borderBottom: "1px solid",
            borderColor: "#808080"
          })}
        />
      )}
    </Box>
  );
}

function DesktopTransparentHeaderNav({ isWhiteText, t }) {
  const { isLoggedIn } = useSession();
  const { pathname } = useLocation();

  const isTeacherPath = pathname === teachPath();
  const isSellerOrgPath =
    pathname === forSellerOrgPath() || pathname === sellerOrgPath();

  return (
    <Box
      flex
      sx={{
        gap: "0.5em"
      }}
    >
      {!isLoggedIn && isTeacherPath && (
        <ExternalLink url={teachTipsUrl()} trackingName="nav_library_link">
          <HeaderNavItem variant={isWhiteText ? "transparent" : "standard"}>
            {t`Educator Library`}
          </HeaderNavItem>
        </ExternalLink>
      )}

      <Link to={leadActivitiesPath()} trackingName="nav_teach_link">
        <HeaderNavItem variant={isWhiteText ? "transparent" : "standard"}>
          {t`Teach`}
        </HeaderNavItem>
      </Link>

      <NavHelpLink>
        <HeaderNavItem variant={isWhiteText ? "transparent" : "standard"}>
          {t`Help`}
        </HeaderNavItem>
      </NavHelpLink>
      {!isLoggedIn && (
        <Link to={Routes.loginPath()} trackingName="nav_login_link">
          <HeaderNavItem variant={isWhiteText ? "transparent" : "standard"}>
            {t`Log In`}
          </HeaderNavItem>
        </Link>
      )}
      {!isLoggedIn && !isSellerOrgPath && (
        <Link
          to={addParamsToUrl(pathFromLocation(window.location), {
            authTrigger: AuthTrigger.JOIN_OUTSCHOOL,
            signup: true
          })}
          trackingName="nav_signup_link"
          useHardNav={true}
        >
          <HeaderNavItem
            variant={isWhiteText ? "transparent" : "standard"}
            sx={{ color: isWhiteText ? "white" : "#4B01D4" }}
          >
            {t`Join`}
          </HeaderNavItem>
        </Link>
      )}
    </Box>
  );
}
